import { useQuery } from '@tanstack/react-query';
import { Users, UserCheck, UserPlus, Calendar, DollarSign } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

interface Stats {
  members: {
    total: number;
    active: number;
    pending: number;
  };
  households: number;
  donations: {
    last30Days: {
      count: number;
      total: number;
    };
  };
  events: {
    upcoming: number;
  };
}

export function DashboardPage() {
  const { user } = useAuth();

  const { data: stats, isLoading } = useQuery({
    queryKey: ['org-stats'],
    queryFn: async () => {
      const response = await api.get<Stats>('/organizations/current/stats');
      if (!response.success) throw new Error(response.error?.message);
      return response.data!;
    },
  });

  const statCards = [
    {
      name: 'Total Members',
      value: stats?.members.total ?? '-',
      icon: Users,
      color: 'bg-blue-500',
    },
    {
      name: 'Active Members',
      value: stats?.members.active ?? '-',
      icon: UserCheck,
      color: 'bg-green-500',
    },
    {
      name: 'Pending Approval',
      value: stats?.members.pending ?? '-',
      icon: UserPlus,
      color: 'bg-yellow-500',
    },
    {
      name: 'Upcoming Events',
      value: stats?.events.upcoming ?? '-',
      icon: Calendar,
      color: 'bg-purple-500',
    },
    {
      name: 'Donations (30 days)',
      value: stats?.donations.last30Days.total
        ? `$${Number(stats.donations.last30Days.total).toLocaleString()}`
        : '-',
      icon: DollarSign,
      color: 'bg-emerald-500',
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600">Welcome back, {user?.member?.firstName || 'Admin'}</p>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="card p-4 animate-pulse">
              <div className="h-10 w-10 bg-gray-200 rounded-lg mb-3" />
              <div className="h-4 bg-gray-200 rounded w-20 mb-2" />
              <div className="h-8 bg-gray-200 rounded w-16" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {statCards.map((stat) => (
            <div key={stat.name} className="card p-4">
              <div className={`inline-flex p-2 rounded-lg ${stat.color} mb-3`}>
                <stat.icon className="h-5 w-5 text-white" />
              </div>
              <p className="text-sm text-gray-500">{stat.name}</p>
              <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
          <div className="space-y-2">
            <button className="w-full btn-outline btn-md justify-start">
              <UserPlus className="h-4 w-4 mr-2" /> Add New Member
            </button>
            <button className="w-full btn-outline btn-md justify-start">
              <Calendar className="h-4 w-4 mr-2" /> Create Event
            </button>
            <button className="w-full btn-outline btn-md justify-start">
              <DollarSign className="h-4 w-4 mr-2" /> Record Donation
            </button>
          </div>
        </div>

        <div className="card p-6">
          <h2 className="text-lg font-semibold mb-4">Recent Activity</h2>
          <p className="text-gray-500 text-sm">Activity feed coming soon...</p>
        </div>
      </div>
    </div>
  );
}
